<template>
    <div v-if="isShow" class="fixed inset-0 bg-white z-[60] flex flex-col w-full h-full overflow-hidden"
        :class="{ 'animate-popupFadeIn': isVisible, 'animate-popupFadeOut': !isVisible }">
        
        <!-- Header -->
        <div class="bg-brand text-white py-3 px-5 font-bold text-[18px] flex items-center justify-between shadow-md">
            <div class="flex items-center gap-2">
                <font-awesome-icon :icon="['fas', 'screwdriver-wrench']" /> Công cụ Admin
            </div>
            <button @click="closePopup()" class="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/90 hover:bg-white hover:text-brand transition-colors focus:outline-none">
                <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
        </div>

        <!-- Body -->
        <div class="flex-1 p-4 md:p-6 overflow-y-auto bg-gray-50/50 no-scrollbar">
            <div class="bg-white p-4 md:p-5 rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-gray-100 max-w-7xl mx-auto">
                <el-tabs v-model="activeTab" class="custom-tabs">
                    <el-tab-pane label="Quản lý người dùng" name="users">
                        <div class="flex justify-between items-center mb-5 mt-2">
                            <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <div class="w-1.5 h-5 bg-brand rounded-full"></div>
                                Danh sách người dùng
                            </h2>
                            <div class="flex gap-2">
                                <el-button type="success" size="small" icon="el-icon-plus" @click="openCreateUserDialog()" class="shadow-[0_2px_4px_rgba(0,0,0,0.05)] font-semibold rounded-lg hover:-translate-y-[1px] transition-transform duration-200 !ml-0">Thêm người dùng</el-button>
                                <el-button type="primary" size="small" icon="el-icon-refresh" @click="fetchData()" :loading="isLoading" class="shadow-[0_2px_4px_rgba(0,0,0,0.05)] font-semibold rounded-lg hover:-translate-y-[1px] transition-transform duration-200 !ml-0">Làm mới</el-button>
                            </div>
                        </div>

                        <div class="relative rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-gray-100">
                            <el-table :data="users" style="width: 100%" v-loading="isLoading" 
                                header-row-class-name="custom-row-head" header-cell-class-name="custom-cell-head"
                                element-loading-text="Đang tải dữ liệu..." element-loading-spinner="el-icon-loading"
                                element-loading-background="rgba(255, 255, 255, 0.8)">
                                <el-table-column prop="name" label="Tên người dùng" min-width="150">
                                    <template slot-scope="scope">
                                        <span class="font-semibold text-gray-800">{{ scope.row.name }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="email" label="Email" min-width="200">
                                    <template slot-scope="scope">
                                        <span class="text-gray-600">{{ scope.row.email }}</span>
                                    </template>
                                </el-table-column>
                        <el-table-column label="Role" width="150" align="center">
                            <template slot-scope="scope">
                                <el-tag size="small" :type="scope.row.user_role ? 'primary' : 'info'" effect="plain">
                                    {{ scope.row.user_role ? scope.row.user_role.name : 'Chưa có' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="Phòng ban" min-width="180">
                            <template slot-scope="scope">
                                <div class="flex flex-wrap gap-1">
                                    <el-tag v-for="dep in scope.row.departments" :key="dep.id" size="mini" type="warning" effect="light">
                                        {{ dep.name }}
                                    </el-tag>
                                    <span v-if="!scope.row.departments || scope.row.departments.length === 0" class="text-gray-400 text-xs italic">Chưa có</span>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="Cửa hàng" width="120" align="center">
                            <template slot-scope="scope">
                                <el-tooltip
                                    v-if="scope.row.stores && scope.row.stores.length > 0"
                                    effect="dark"
                                    placement="top"
                                    :content="scope.row.stores.map(s => s.shortaddress || s.code).join('\n')">
                                    <el-tag type="info" size="small" class="cursor-pointer">
                                        <font-awesome-icon :icon="['fas', 'store']" class="mr-1 text-[10px]" />
                                        {{ scope.row.stores.length }} cửa hàng
                                    </el-tag>
                                </el-tooltip>
                                <span v-else class="text-gray-400 text-xs italic">Chưa có</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="Kích hoạt" width="140" align="center">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.is_active ? 'success' : 'danger'" size="small" effect="dark">
                                    {{ scope.row.is_active ? 'Đã kích hoạt' : 'Bị khóa' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="Setup OTP (2FA)" width="150" align="center">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.is_active_2fa ? 'success' : 'info'" size="small">
                                    {{ scope.row.is_active_2fa ? 'Đã cài đặt' : 'Chưa cài' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="MAX_SIZE_FILE" label="Max File (MB)" width="120" align="center">
                            <template slot-scope="scope">
                                <span class="font-semibold text-gray-700">{{ scope.row.MAX_SIZE_FILE || 5 }} MB</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="Thao tác" width="100" align="center" fixed="right">
                            <template slot-scope="scope">
                                <el-button size="mini" type="primary" icon="el-icon-edit" circle @click="openEditDialog(scope.row)"></el-button>
                            </template>
                        </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>

                    <el-tab-pane label="Quản lý Chức vụ" name="roles">
                        <div class="flex justify-between items-center mb-5 mt-2">
                            <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <div class="w-1.5 h-5 bg-brand rounded-full"></div>
                                Danh sách chức vụ
                            </h2>
                            <el-button type="primary" size="small" icon="el-icon-plus" @click="openRoleDialog(null)" class="shadow-[0_2px_4px_rgba(0,0,0,0.05)] font-semibold rounded-lg hover:-translate-y-[1px] transition-transform duration-200 !ml-0">Tạo chức vụ</el-button>
                        </div>

                        <div class="relative rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-gray-100">
                            <el-table :data="roles" style="width: 100%" v-loading="isLoading" 
                                header-row-class-name="custom-row-head" header-cell-class-name="custom-cell-head"
                                element-loading-text="Đang tải dữ liệu..." element-loading-spinner="el-icon-loading"
                                element-loading-background="rgba(255, 255, 255, 0.8)">
                                <el-table-column prop="name" label="Tên chức vụ" min-width="150">
                                    <template slot-scope="scope">
                                        <span class="font-semibold text-gray-800">{{ scope.row.name }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="value" label="Giá trị (Value)" width="150" align="center"></el-table-column>
                                <el-table-column label="Thao tác" width="120" align="center" fixed="right">
                                    <template slot-scope="scope">
                                        <div class="flex items-center justify-center gap-2">
                                            <el-button size="mini" type="primary" icon="el-icon-edit" circle @click="openRoleDialog(scope.row)"></el-button>
                                            <el-button size="mini" type="danger" icon="el-icon-delete" circle @click="deleteRole(scope.row)"></el-button>
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                        
                        <!-- Ghi chú các chức vụ mặc định -->
                        <div class="mt-6 p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
                            <h3 class="flex items-center gap-2 text-sm font-bold text-blue-800 mb-3">
                                <font-awesome-icon :icon="['fas', 'circle-info']" />
                                Ghi chú: Kế hoạch phân quyền theo giá trị (Value)
                            </h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                                <div class="flex items-start gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">0</span>
                                    <span class="leading-relaxed"><strong>Giám đốc</strong><br><span class="text-xs text-gray-500">Quyền hạn cao nhất toàn hệ thống</span></span>
                                </div>
                                <div class="flex items-start gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">1</span>
                                    <span class="leading-relaxed"><strong>Kế toán trưởng</strong><br><span class="text-xs text-gray-500">Toàn quyền xét duyệt nghiệp vụ Kế toán</span></span>
                                </div>
                                <div class="flex items-start gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">2</span>
                                    <span class="leading-relaxed"><strong>Kế toán thu chi / Quản lý</strong><br><span class="text-xs text-gray-500">Tạo phiếu, quản lý cấp trung gian</span></span>
                                </div>
                                <div class="flex items-start gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">3</span>
                                    <span class="leading-relaxed"><strong>Nhân viên</strong><br><span class="text-xs text-gray-500">Quyền cơ bản của nhân viên thông thường</span></span>
                                </div>
                            </div>
                        </div>

                    </el-tab-pane>
                    <el-tab-pane label="Quản lý Phòng ban" name="departments">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="text-lg font-bold text-gray-800">Danh sách Phòng ban</h3>
                            <el-button type="primary" size="medium" icon="el-icon-plus" class="rounded-lg shadow-sm font-semibold" @click="openDepartmentDialog()">Tạo mới</el-button>
                        </div>
                        
                        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <el-table :data="departments" style="width: 100%" v-loading="isLoading" 
                                element-loading-text="Đang tải dữ liệu..." element-loading-spinner="el-icon-loading"
                                element-loading-background="rgba(255, 255, 255, 0.8)">
                                <el-table-column prop="name" label="Tên phòng ban" min-width="150">
                                    <template slot-scope="scope">
                                        <span class="font-semibold text-gray-800">{{ scope.row.name }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="value" label="Giá trị (Value)" width="150" align="center"></el-table-column>
                                <el-table-column label="Thao tác" width="120" align="center" fixed="right">
                                    <template slot-scope="scope">
                                        <div class="flex items-center justify-center gap-2">
                                            <el-button size="mini" type="primary" icon="el-icon-edit" circle @click="openDepartmentDialog(scope.row)"></el-button>
                                            <el-button size="mini" type="danger" icon="el-icon-delete" circle @click="deleteDepartment(scope.row)"></el-button>
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                        
                        <!-- Ghi chú các phòng ban mặc định -->
                        <div class="mt-6 p-4 bg-green-50/50 border border-green-100 rounded-xl">
                            <h3 class="flex items-center gap-2 text-sm font-bold text-green-800 mb-3">
                                <font-awesome-icon :icon="['fas', 'circle-info']" />
                                Ghi chú: Danh sách phòng ban và Giá trị định danh (Value)
                            </h3>
                            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm text-gray-700">
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">0</span>
                                    <span class="leading-relaxed"><strong>Tất cả</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">1</span>
                                    <span class="leading-relaxed"><strong>Phòng IT</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">2</span>
                                    <span class="leading-relaxed"><strong>Phòng Marketing</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">3</span>
                                    <span class="leading-relaxed"><strong>Phòng Kế Toán</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">4</span>
                                    <span class="leading-relaxed"><strong>Phòng Đào tạo</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">5</span>
                                    <span class="leading-relaxed"><strong>Phòng Kinh doanh</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">6</span>
                                    <span class="leading-relaxed"><strong>Phòng Hệ thống</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">7</span>
                                    <span class="leading-relaxed"><strong>Phòng Thi công - Bảo trì</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">8</span>
                                    <span class="leading-relaxed"><strong>Kho HIGHLAND AGRI</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">9</span>
                                    <span class="leading-relaxed"><strong>Phòng HC-NS</strong></span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-brand bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100 min-w-[28px] text-center">11</span>
                                    <span class="leading-relaxed"><strong>BOD</strong></span>
                                </div>
                            </div>
                        </div>

                    </el-tab-pane>

                    <el-tab-pane label="Quản lý Công ty" name="companies">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="text-lg font-bold text-gray-800">Danh sách Công ty</h3>
                            <el-button type="primary" size="medium" icon="el-icon-plus" class="rounded-lg shadow-sm font-semibold" @click="openCompanyDialog()">Tạo mới</el-button>
                        </div>
                        
                        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <el-table :data="companies" style="width: 100%" v-loading="isLoading" 
                                element-loading-text="Đang tải dữ liệu..." element-loading-spinner="el-icon-loading"
                                element-loading-background="rgba(255, 255, 255, 0.8)">
                                <el-table-column prop="name" label="Tên công ty" min-width="150">
                                    <template slot-scope="scope">
                                        <span class="font-semibold text-gray-800">{{ scope.row.name }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="value" label="Giá trị (Value)" width="150" align="center"></el-table-column>
                                <el-table-column label="Thao tác" width="120" align="center" fixed="right">
                                    <template slot-scope="scope">
                                        <div class="flex items-center justify-center gap-2">
                                            <el-button size="mini" type="primary" icon="el-icon-edit" circle @click="openCompanyDialog(scope.row)"></el-button>
                                            <el-button size="mini" type="danger" icon="el-icon-delete" circle @click="deleteCompany(scope.row)"></el-button>
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>

                    <!-- Tab: Quản lý Cửa hàng -->
                    <el-tab-pane label="Quản lý Cửa hàng" name="stores">
                        <div class="flex justify-between items-center mb-5 mt-2">
                            <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <div class="w-1.5 h-5 bg-brand rounded-full"></div>
                                Danh sách Cửa hàng
                            </h2>
                            <div class="flex gap-2 items-center">
                                <el-input v-model="storeSearch" placeholder="Tìm kiếm..." size="small" prefix-icon="el-icon-search" clearable @clear="fetchStores" @keyup.enter.native="fetchStores" class="w-[200px]"></el-input>
                                <el-button type="success" size="small" icon="el-icon-plus" @click="openStoreDialog()" class="shadow-[0_2px_4px_rgba(0,0,0,0.05)] font-semibold rounded-lg hover:-translate-y-[1px] transition-transform duration-200 !ml-0">Thêm cửa hàng</el-button>
                                <el-button type="primary" size="small" icon="el-icon-refresh" @click="fetchStores()" :loading="isLoadingStores" class="shadow-[0_2px_4px_rgba(0,0,0,0.05)] font-semibold rounded-lg hover:-translate-y-[1px] transition-transform duration-200 !ml-0">Làm mới</el-button>
                            </div>
                        </div>

                        <div class="relative rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-gray-100">
                            <el-table :data="stores" style="width: 100%" v-loading="isLoadingStores"
                                header-row-class-name="custom-row-head" header-cell-class-name="custom-cell-head"
                                element-loading-text="Đang tải..." element-loading-spinner="el-icon-loading"
                                element-loading-background="rgba(255, 255, 255, 0.8)">
                                <el-table-column prop="id" label="ID" width="70" align="center">
                                    <template slot-scope="scope">
                                        <span class="text-gray-400 text-xs font-mono">#{{ scope.row.id }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="code" label="Mã" width="100" align="center">
                                    <template slot-scope="scope">
                                        <span class="font-mono font-semibold text-brand text-sm">{{ scope.row.code }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="shortaddress" label="Tên cửa hàng" min-width="160">
                                    <template slot-scope="scope">
                                        <span class="font-semibold text-gray-800">{{ scope.row.shortaddress }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="fulladdress" label="Địa chỉ đầy đủ" min-width="220">
                                    <template slot-scope="scope">
                                        <span class="text-gray-600 text-sm">{{ scope.row.fulladdress }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column label="Thương hiệu" width="130" align="center">
                                    <template slot-scope="scope">
                                        <el-tag v-if="scope.row.brand && scope.row.brand.name" size="small" type="info" effect="plain">{{ scope.row.brand.name }}</el-tag>
                                        <span v-else class="text-gray-400 text-xs">—</span>
                                    </template>
                                </el-table-column>
                                <el-table-column label="Store ID" width="100" align="center">
                                    <template slot-scope="scope">
                                        <span class="text-gray-500 text-xs font-mono">{{ scope.row.storeid || '—' }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column label="Thao tác" width="100" align="center" fixed="right">
                                    <template slot-scope="scope">
                                        <div class="flex items-center justify-center gap-2">
                                            <el-button size="mini" type="primary" icon="el-icon-edit" circle @click="openStoreDialog(scope.row)"></el-button>
                                            <el-button size="mini" type="danger" icon="el-icon-delete" circle @click="deleteStore(scope.row)"></el-button>
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>

                        <!-- Pagination -->
                        <div class="flex justify-end mt-4" v-if="storePagination.total > storePagination.pageSize">
                            <el-pagination
                                background
                                layout="prev, pager, next"
                                :total="storePagination.total"
                                :page-size="storePagination.pageSize"
                                :current-page="storePagination.page"
                                @current-change="onStorePageChange">
                            </el-pagination>
                        </div>
                    </el-tab-pane>

                    <el-tab-pane label="Giao diện (Theme)" name="theme">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="text-lg font-bold text-gray-800">Cài đặt giao diện</h3>
                            <el-button type="primary" size="medium" icon="el-icon-check" class="rounded-lg shadow-sm font-semibold" @click="saveTheme()" :loading="isSavingTheme">Lưu cài đặt</el-button>
                        </div>
                        <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
                            <div class="mb-6">
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Tên ứng dụng (App Name)</label>
                                <el-input v-model="themeForm.appName" placeholder="Nhập tên ứng dụng (vd: HIGHLAND AGRI)" clearable></el-input>
                                <div class="text-xs text-gray-400 mt-2">Tên này sẽ được hiển thị ở trang đăng nhập và các tiêu đề chính.</div>
                            </div>
                            <div class="mb-6">
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Màu chủ đạo (Brand Color)</label>
                                <div class="flex items-center gap-4">
                                    <el-color-picker v-model="themeForm.brandColor" color-format="hex"></el-color-picker>
                                    <span class="text-gray-500 text-sm">Màu hiện tại: <span class="font-bold text-gray-800">{{ themeForm.brandColor || 'Chưa cài đặt' }}</span></span>
                                </div>
                                <div class="text-xs text-gray-400 mt-2">Màu này sẽ được áp dụng cho các thanh tiêu đề, nút bấm, và các thành phần chính của dự án.</div>
                            </div>
                            <div class="mb-6">
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Logo hệ thống</label>
                                
                                <div v-if="!themeForm.logoUrl" class="relative inline-block w-full mb-2">
                                    <div class="w-full p-4 border-2 border-dashed border-brand/40 rounded-xl text-brand font-semibold bg-brand/5 hover:bg-brand hover:text-white transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                                        <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" class="text-xl" /> 
                                        <span>Kéo thả hoặc nhấp để tải ảnh Logo lên</span>
                                        <i v-if="isUploadingLogo" class="el-icon-loading ml-2"></i>
                                    </div>
                                    <input type="file" ref="logoInput" class="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" @change="handleLogoUpload" :disabled="isUploadingLogo">
                                </div>

                                <div v-if="themeForm.logoUrl" class="flex items-center justify-between border border-gray-200 bg-gray-50 px-4 py-3 rounded-xl shadow-sm mb-2">
                                    <div class="flex items-center gap-4">
                                        <div class="bg-white p-2 border border-gray-200 rounded-lg shadow-sm">
                                            <img :src="themeForm.logoUrl" class="h-10 w-auto object-contain" alt="Logo preview" />
                                        </div>
                                        <div>
                                            <span class="block text-sm font-bold text-gray-800">Logo hiện tại</span>
                                            <span class="block text-xs text-gray-500">Hình ảnh sẽ được hiển thị ở thanh tiêu đề chính.</span>
                                        </div>
                                    </div>
                                    <button class="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2.5 rounded-lg transition-colors shrink-0 border border-transparent hover:border-red-100" @click="removeLogo" title="Xoá Logo" :disabled="isUploadingLogo">
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </div>
                                <div class="text-xs text-gray-400 mt-2">Để trống nếu muốn sử dụng logo mặc định của hệ thống.</div>
                            </div>
                            
                            <div class="mb-6">
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Logo trang đăng nhập (Login Logo)</label>
                                
                                <div v-if="!themeForm.loginLogoUrl" class="relative inline-block w-full mb-2">
                                    <div class="w-full p-4 border-2 border-dashed border-brand/40 rounded-xl text-brand font-semibold bg-brand/5 hover:bg-brand hover:text-white transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                                        <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" class="text-xl" /> 
                                        <span>Kéo thả hoặc nhấp để tải Logo trang đăng nhập lên</span>
                                        <i v-if="isUploadingLoginLogo" class="el-icon-loading ml-2"></i>
                                    </div>
                                    <input type="file" ref="loginLogoInput" class="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" @change="handleLoginLogoUpload" :disabled="isUploadingLoginLogo">
                                </div>

                                <div v-if="themeForm.loginLogoUrl" class="flex items-center justify-between border border-gray-200 bg-gray-50 px-4 py-3 rounded-xl shadow-sm mb-2">
                                    <div class="flex items-center gap-4">
                                        <div class="bg-white p-2 border border-gray-200 rounded-lg shadow-sm">
                                            <img :src="themeForm.loginLogoUrl" class="h-10 w-auto object-contain" alt="Login logo preview" />
                                        </div>
                                        <div>
                                            <span class="block text-sm font-bold text-gray-800">Logo đăng nhập hiện tại</span>
                                            <span class="block text-xs text-gray-500">Tách biệt với logo bên trong ứng dụng.</span>
                                        </div>
                                    </div>
                                    <button class="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2.5 rounded-lg transition-colors shrink-0 border border-transparent hover:border-red-100" @click="removeLoginLogo" title="Xoá Logo trang đăng nhập" :disabled="isUploadingLoginLogo">
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </div>
                                <div class="text-xs text-gray-400 mt-2">Để trống nếu muốn sử dụng chung logo hệ thống.</div>
                            </div>
                            
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Ảnh tiêu đề (Header Bill)</label>
                                
                                <div v-if="!themeForm.headerBillUrl" class="relative inline-block w-full mb-2">
                                    <div class="w-full p-4 border-2 border-dashed border-brand/40 rounded-xl text-brand font-semibold bg-brand/5 hover:bg-brand hover:text-white transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                                        <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" class="text-xl" /> 
                                        <span>Kéo thả hoặc nhấp để tải ảnh Header Bill lên</span>
                                        <i v-if="isUploadingHeaderBill" class="el-icon-loading ml-2"></i>
                                    </div>
                                    <input type="file" ref="headerBillInput" class="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" @change="handleHeaderBillUpload" :disabled="isUploadingHeaderBill">
                                </div>

                                <div v-if="themeForm.headerBillUrl" class="flex items-center justify-between border border-gray-200 bg-gray-50 px-4 py-3 rounded-xl shadow-sm mb-2">
                                    <div class="flex items-center gap-4">
                                        <div class="bg-white p-2 border border-gray-200 rounded-lg shadow-sm">
                                            <img :src="themeForm.headerBillUrl" class="h-10 w-auto object-contain" alt="Header Bill preview" />
                                        </div>
                                        <div>
                                            <span class="block text-sm font-bold text-gray-800">Ảnh tiêu đề hiện tại</span>
                                            <span class="block text-xs text-gray-500">Hình ảnh sẽ hiển thị trên cùng của phiếu chi/thanh toán.</span>
                                        </div>
                                    </div>
                                    <button class="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2.5 rounded-lg transition-colors shrink-0 border border-transparent hover:border-red-100" @click="removeHeaderBill" title="Xoá Ảnh tiêu đề" :disabled="isUploadingHeaderBill">
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </div>
                                <div class="text-xs text-gray-400 mt-2">Để trống nếu muốn sử dụng ảnh mặc định của hệ thống. Kích thước khuyến nghị: ngang (landscape).</div>
                            </div>

                            <!-- Login Page Text Section -->
                            <div class="mt-6 pt-6 border-t border-gray-100">
                                <h4 class="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                                    <font-awesome-icon :icon="['fas', 'pen-to-square']" class="text-brand" />
                                    Nội dung trang Đăng nhập (Login Page)
                                </h4>
                                <div class="space-y-4">
                                    <div>
                                        <label class="block text-sm font-semibold text-gray-700 mb-2">Nhãn Portal <span class="text-gray-400 text-xs font-normal">(Portal Label - dòng nhỏ phía trên tiêu đề)</span></label>
                                        <el-input v-model="themeForm.loginPortalLabel" placeholder="VD: PORTAL MANAGEMENT" clearable></el-input>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-semibold text-gray-700 mb-2">Tiêu đề chính <span class="text-gray-400 text-xs font-normal">(Login Title)</span></label>
                                        <el-input v-model="themeForm.loginTitle" type="textarea" :rows="2" placeholder="VD: Hệ Thống Phiếu Chi Điện Tử"></el-input>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-semibold text-gray-700 mb-2">Mô tả <span class="text-gray-400 text-xs font-normal">(Subtitle)</span></label>
                                        <el-input v-model="themeForm.loginSubtitle" type="textarea" :rows="3" placeholder="VD: Giải pháp quản lý tài chính toàn diện..."></el-input>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-semibold text-gray-700 mb-2">Copyright <span class="text-gray-400 text-xs font-normal">(Dòng footer phía dưới)</span></label>
                                        <el-input v-model="themeForm.loginCopyright" placeholder="VD: © 2024 HIGHLAND AGRI. All rights reserved." clearable></el-input>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </div>

        <!-- Create User Modal -->
        <div v-if="createUserDialogVisible" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] transition-all duration-300">
            <div class="relative bg-white/95 backdrop-blur-md md:rounded-2xl shadow-2xl border border-white/20 md:w-[500px] w-full flex flex-col overflow-hidden animate-popupFadeIn">
                <!-- Header -->
                <h2 class="relative w-full bg-brand p-4 text-center text-yellow-500 font-bold text-[16px] tracking-wide flex-shrink-0" style="background-color: var(--color-brand);">
                    Thêm người dùng mới
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 text-white/90 hover:text-white transition-colors flex items-center justify-center cursor-pointer shadow-sm" @click="createUserDialogVisible = false">
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                </h2>

                <!-- Content -->
                <div class="p-6 overflow-y-auto max-h-[75vh]">
                    <div class="space-y-5">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Họ và tên <span class="text-red-500">*</span></label>
                            <el-input v-model="createUserForm.name" placeholder="VD: Nguyễn Văn A" clearable></el-input>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Email <span class="text-red-500">*</span></label>
                            <el-input v-model="createUserForm.email" placeholder="VD: nhanvien@company.com" type="email" clearable></el-input>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Mật khẩu <span class="text-red-500">*</span></label>
                            <el-input v-model="createUserForm.password" placeholder="Nhập mật khẩu" :type="showCreatePassword ? 'text' : 'password'">
                                <i slot="suffix" class="el-input__icon cursor-pointer" :class="showCreatePassword ? 'el-icon-view' : 'el-icon-minus'" @click="showCreatePassword = !showCreatePassword"></i>
                            </el-input>
                            <div class="text-xs text-gray-400 mt-1">Mật khẩu sẽ được mã hóa an toàn trước khi lưu.</div>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Chức vụ</label>
                            <el-select v-model="createUserForm.user_role_id" placeholder="Chọn chức vụ" class="w-full" clearable>
                                <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id"></el-option>
                            </el-select>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Phòng ban</label>
                            <el-select v-model="createUserForm.department_ids" multiple placeholder="Chọn phòng ban" class="w-full">
                                <el-option v-for="dep in departments" :key="dep.id" :label="dep.name" :value="dep.id"></el-option>
                            </el-select>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Dung lượng file tối đa <span class="text-gray-400 text-xs font-normal">(MB)</span></label>
                            <el-input-number v-model="createUserForm.MAX_SIZE_FILE" :min="1" :max="1024" class="w-full" controls-position="right"></el-input-number>
                        </div>
                        <div class="flex items-center justify-between py-3 border-t border-gray-100">
                            <label class="text-sm font-semibold text-gray-700">Kích hoạt tài khoản ngay</label>
                            <el-switch v-model="createUserForm.is_active" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="sticky bottom-0 w-full flex items-center justify-center gap-3 p-4 border-t border-gray-100 bg-white/95 backdrop-blur-md md:rounded-b-2xl shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <button @click="saveNewUser()" :disabled="isCreatingUser" class="min-w-[140px] py-2.5 px-6 bg-brand text-white font-bold rounded-xl shadow-[0_4px_10px_rgba(2,75,179,0.3)] hover:-translate-y-[1px] hover:bg-brand-light hover:shadow-[0_6px_14px_rgba(2,75,179,0.4)] transition-all flex justify-center items-center">
                        <span v-if="!isCreatingUser">Tạo tài khoản</span>
                        <div v-else class="flex text-sm items-center gap-2"><i class="el-icon-loading"></i> Đang tạo...</div>
                    </button>
                    <button @click="createUserDialogVisible = false" class="min-w-[100px] py-2.5 px-6 font-bold rounded-xl border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex justify-center items-center">Đóng</button>
                </div>
            </div>
        </div>

        <!-- Store Create/Edit Modal -->
        <div v-if="storeDialogVisible" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] transition-all duration-300">
            <div class="relative bg-white/95 backdrop-blur-md md:rounded-2xl shadow-2xl border border-white/20 md:w-[560px] w-full flex flex-col overflow-hidden animate-popupFadeIn">
                <!-- Header -->
                <h2 class="relative w-full bg-brand p-4 text-center text-yellow-500 font-bold text-[16px] tracking-wide flex-shrink-0" style="background-color: var(--color-brand);">
                    {{ editingStore ? 'Cập nhật Cửa hàng' : 'Thêm Cửa hàng mới' }}
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 text-white/90 hover:text-white transition-colors flex items-center justify-center cursor-pointer shadow-sm" @click="storeDialogVisible = false">
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                </h2>

                <!-- Content -->
                <div class="p-6 overflow-y-auto max-h-[75vh]">
                    <div class="space-y-4">
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Mã cửa hàng (Code) <span class="text-red-500">*</span></label>
                                <el-input v-model="storeForm.code" placeholder="VD: GU001" clearable :disabled="!!editingStore"></el-input>
                                <div v-if="editingStore" class="text-xs text-gray-400 mt-1">Không thể thay đổi mã.</div>
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-2">Store ID <span class="text-gray-400 text-xs font-normal">(tùy chọn)</span></label>
                                <el-input-number v-model="storeForm.storeid" :min="0" class="w-full" controls-position="right" placeholder="ID từ hệ thống ngoài"></el-input-number>
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Tên cửa hàng (ngắn) <span class="text-red-500">*</span></label>
                            <el-input v-model="storeForm.shortaddress" placeholder="VD: HIGHLAND AGRI Hoàng Hoa Thám" clearable></el-input>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Địa chỉ đầy đủ <span class="text-red-500">*</span></label>
                            <el-input v-model="storeForm.fulladdress" type="textarea" :rows="2" placeholder="VD: 123 Hoàng Hoa Thám, Q. Bình Thạnh, TP.HCM"></el-input>
                        </div>
                        <div class="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Thông tin Thương hiệu (Brand)</div>
                            <div class="grid grid-cols-2 gap-3">
                                <div>
                                    <label class="block text-xs font-semibold text-gray-600 mb-1">Tên thương hiệu</label>
                                    <el-input v-model="storeForm.brandName" placeholder="VD: HIGHLAND AGRI" size="small" clearable></el-input>
                                </div>
                                <div>
                                    <label class="block text-xs font-semibold text-gray-600 mb-1">Mã thương hiệu</label>
                                    <el-input v-model="storeForm.brandCode" placeholder="VD: GC" size="small" clearable></el-input>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="sticky bottom-0 w-full flex items-center justify-center gap-3 p-4 border-t border-gray-100 bg-white/95 backdrop-blur-md md:rounded-b-2xl shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <button @click="saveStore()" :disabled="isSavingStore" class="min-w-[140px] py-2.5 px-6 bg-brand text-white font-bold rounded-xl shadow-[0_4px_10px_rgba(2,75,179,0.3)] hover:-translate-y-[1px] hover:bg-brand-light hover:shadow-[0_6px_14px_rgba(2,75,179,0.4)] transition-all flex justify-center items-center">
                        <span v-if="!isSavingStore">{{ editingStore ? 'Cập nhật' : 'Tạo cửa hàng' }}</span>
                        <div v-else class="flex text-sm items-center gap-2"><i class="el-icon-loading"></i> Đang lưu...</div>
                    </button>
                    <button @click="storeDialogVisible = false" class="min-w-[100px] py-2.5 px-6 font-bold rounded-xl border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex justify-center items-center">Đóng</button>
                </div>
            </div>
        </div>

        <!-- Edit User Modal -->
        <div v-if="editDialogVisible" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] transition-all duration-300">
            <div class="relative bg-white/95 backdrop-blur-md md:rounded-2xl shadow-2xl border border-white/20 md:w-[500px] w-full flex flex-col overflow-hidden animate-popupFadeIn">
                <!-- Header -->
                <h2 class="relative w-full bg-brand p-4 text-center text-yellow-500 font-bold text-[16px] tracking-wide flex-shrink-0" style="background-color: var(--color-brand);">
                    Cập nhật thông tin User
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 text-white/90 hover:text-white transition-colors flex items-center justify-center cursor-pointer shadow-sm" @click="editDialogVisible = false">
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                </h2>
                
                <!-- Content -->
                <div class="p-6 overflow-y-auto max-h-[70vh]">
                    <div v-if="editingUser" class="space-y-5">
                        <div class="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div>
                                <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Email <span class="text-red-500">*</span></label>
                                <el-input v-model="editForm.email" placeholder="Nhập địa chỉ email" clearable size="medium"></el-input>
                            </div>
                            <div>
                                <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Tên tài khoản / Người dùng <span class="text-red-500">*</span></label>
                                <el-input v-model="editForm.name" placeholder="Nhập tên người dùng" clearable size="medium"></el-input>
                            </div>
                            <div>
                                <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Mật khẩu mới <span class="text-gray-400 font-normal lowercase">(để trống nếu không đổi)</span></label>
                                <el-input v-model="editForm.password" type="password" show-password placeholder="Nhập mật khẩu mới..." size="medium"></el-input>
                            </div>
                        </div>

                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">User Role (Chức vụ)</label>
                            <el-select v-model="editForm.user_role_id" placeholder="Chọn chức vụ" class="w-full" clearable>
                                <el-option
                                    v-for="role in roles"
                                    :key="role.id"
                                    :label="role.name"
                                    :value="role.id">
                                </el-option>
                            </el-select>
                        </div>

                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Departments (Phòng ban)</label>
                            <el-select v-model="editForm.department_ids" multiple placeholder="Chọn phòng ban" class="w-full">
                                <el-option
                                    v-for="dep in departments"
                                    :key="dep.id"
                                    :label="dep.name"
                                    :value="dep.id">
                                </el-option>
                            </el-select>
                        </div>

                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Cửa hàng (Stores)</label>
                            <el-select
                                v-model="editForm.store_ids"
                                multiple
                                filterable
                                placeholder="Chọn cửa hàng"
                                class="w-full"
                                :loading="isLoadingStoreOptions"
                                no-data-text="Không có dữ liệu">
                                <el-option
                                    v-for="s in storeOptions"
                                    :key="s.id"
                                    :label="s.shortaddress + (s.code ? ' (' + s.code + ')' : '')"
                                    :value="s.id">
                                </el-option>
                            </el-select>
                            <div class="text-xs text-gray-400 mt-1">Chọn các cửa hàng mà người dùng có quyền truy cập.</div>
                        </div>

                        <div class="flex flex-col gap-4 py-4 border-t border-b border-gray-100">
                            <div class="flex items-center justify-between">
                                <label class="text-sm flex-1 font-semibold text-gray-700">Kích hoạt tài khoản <span class="text-gray-400 text-xs font-normal ml-1">(is_active)</span></label>
                                <el-switch v-model="editForm.is_active" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
                            </div>
                            <div class="flex items-center justify-between">
                                <label class="text-sm flex-1 font-semibold text-gray-700">Đã cài đặt OTP <span class="text-gray-400 text-xs font-normal ml-1">(is_active_2fa)</span></label>
                                <el-switch v-model="editForm.is_active_2fa" active-color="#13ce66"></el-switch>
                            </div>
                        </div>

                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Dung lượng tải file <span class="text-gray-400 text-xs font-normal ml-1">(MAX_SIZE_FILE)</span></label>
                            <el-input-number v-model="editForm.MAX_SIZE_FILE" :min="1" :max="1024" class="w-full" controls-position="right">
                                <template slot="append">MB</template>
                            </el-input-number>
                            <div class="text-xs text-gray-400 mt-2">Lưu ý: Đơn vị tính là Megabyte (MB).</div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="sticky bottom-0 w-full flex items-center justify-center gap-3 p-4 border-t border-gray-100 bg-white/95 backdrop-blur-md md:rounded-b-2xl shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <button @click="saveUser()" :disabled="isSaving" class="min-w-[140px] py-2.5 px-6 bg-brand text-white font-bold rounded-xl shadow-[0_4px_10px_rgba(2,75,179,0.3)] hover:-translate-y-[1px] hover:bg-brand-light hover:shadow-[0_6px_14px_rgba(2,75,179,0.4)] transition-all flex justify-center items-center">
                        <span v-if="!isSaving">Cập nhật ngay</span>
                        <div v-else class="flex text-sm items-center gap-2">
                            <i class="el-icon-loading"></i> Đang tải
                        </div>
                    </button>
                    <button @click="editDialogVisible = false" class="min-w-[100px] py-2.5 px-6 font-bold rounded-xl border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex justify-center items-center">
                        Đóng
                    </button>
                </div>
            </div>
        </div>

        <!-- Edit/Add Role Modal -->
        <div v-if="roleDialogVisible" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] transition-all duration-300">
            <div class="relative bg-white/95 backdrop-blur-md md:rounded-2xl shadow-2xl border border-white/20 md:w-[400px] w-full flex flex-col overflow-hidden animate-popupFadeIn">
                <!-- Header -->
                <h2 class="relative w-full bg-brand p-4 text-center text-yellow-500 font-bold text-[16px] tracking-wide flex-shrink-0" style="background-color: var(--color-brand);">
                    {{ editingRole ? 'Cập nhật Chức vụ' : 'Tạo mới Chức vụ' }}
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 text-white/90 hover:text-white transition-colors flex items-center justify-center cursor-pointer shadow-sm" @click="roleDialogVisible = false">
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                </h2>
                
                <!-- Content -->
                <div class="p-6 overflow-y-auto max-h-[70vh]">
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Tên chức vụ (Name)</label>
                            <el-input v-model="roleForm.name" placeholder="VD: Giao hàng"></el-input>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Giá trị (Value)</label>
                            <el-input-number v-model="roleForm.value" :min="0" :max="99" class="w-full" controls-position="right"></el-input-number>
                            <div class="text-xs text-gray-400 mt-2">Lưu ý: Giá trị số dùng để phân quyền trong code. 0: Giám đốc, 1: Kế toán trưởng, 2: Kế toán thu chi/Quản lý cấp trung, 3: Nhân viên</div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="sticky bottom-0 w-full flex items-center justify-center gap-3 p-4 border-t border-gray-100 bg-white/95 backdrop-blur-md md:rounded-b-2xl shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <button @click="saveRole()" :disabled="isSavingRole" class="min-w-[140px] py-2.5 px-6 bg-brand text-white font-bold rounded-xl shadow-[0_4px_10px_rgba(2,75,179,0.3)] hover:-translate-y-[1px] hover:bg-brand-light hover:shadow-[0_6px_14px_rgba(2,75,179,0.4)] transition-all flex justify-center items-center">
                        <span v-if="!isSavingRole">{{ editingRole ? 'Cập nhật' : 'Tạo mới' }}</span>
                        <div v-else class="flex text-sm items-center gap-2">
                            <i class="el-icon-loading"></i> Đang tải
                        </div>
                    </button>
                    <button @click="roleDialogVisible = false" class="min-w-[100px] py-2.5 px-6 font-bold rounded-xl border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex justify-center items-center">
                        Đóng
                    </button>
                </div>
            </div>
        </div>

        <!-- Edit/Add Department Modal -->
        <div v-if="departmentDialogVisible" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] transition-all duration-300">
            <div class="relative bg-white/95 backdrop-blur-md md:rounded-2xl shadow-2xl border border-white/20 md:w-[400px] w-full flex flex-col overflow-hidden animate-popupFadeIn">
                <!-- Header -->
                <h2 class="relative w-full bg-brand p-4 text-center text-yellow-500 font-bold text-[16px] tracking-wide flex-shrink-0" style="background-color: var(--color-brand);">
                    {{ editingDepartment ? 'Cập nhật Phòng ban' : 'Tạo mới Phòng ban' }}
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 text-white/90 hover:text-white transition-colors flex items-center justify-center cursor-pointer shadow-sm" @click="departmentDialogVisible = false">
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                </h2>
                
                <!-- Content -->
                <div class="p-6 overflow-y-auto max-h-[70vh]">
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Tên phòng ban (Name)</label>
                            <el-input v-model="departmentForm.name" placeholder="VD: Marketing"></el-input>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Giá trị (Value)</label>
                            <el-input-number v-model="departmentForm.value" :min="0" :max="99" class="w-full" controls-position="right"></el-input-number>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="sticky bottom-0 w-full flex items-center justify-center gap-3 p-4 border-t border-gray-100 bg-white/95 backdrop-blur-md md:rounded-b-2xl shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <button @click="saveDepartment()" :disabled="isSavingDepartment" class="min-w-[140px] py-2.5 px-6 bg-brand text-white font-bold rounded-xl shadow-[0_4px_10px_rgba(2,75,179,0.3)] hover:-translate-y-[1px] hover:bg-brand-light hover:shadow-[0_6px_14px_rgba(2,75,179,0.4)] transition-all flex justify-center items-center">
                        <span v-if="!isSavingDepartment">{{ editingDepartment ? 'Cập nhật' : 'Tạo mới' }}</span>
                        <div v-else class="flex text-sm items-center gap-2">
                            <i class="el-icon-loading"></i> Đang tải
                        </div>
                    </button>
                    <button @click="departmentDialogVisible = false" class="min-w-[100px] py-2.5 px-6 font-bold rounded-xl border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex justify-center items-center">
                        Đóng
                    </button>
                </div>
            </div>
        </div>

        <!-- Edit/Add Company Modal -->
        <div v-if="companyDialogVisible" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] transition-all duration-300">
            <div class="relative bg-white/95 backdrop-blur-md md:rounded-2xl shadow-2xl border border-white/20 md:w-[400px] w-full flex flex-col overflow-hidden animate-popupFadeIn">
                <!-- Header -->
                <h2 class="relative w-full bg-brand p-4 text-center text-yellow-500 font-bold text-[16px] tracking-wide flex-shrink-0" style="background-color: var(--color-brand);">
                    {{ editingCompany ? 'Cập nhật Công ty' : 'Tạo mới Công ty' }}
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 text-white/90 hover:text-white transition-colors flex items-center justify-center cursor-pointer shadow-sm" @click="companyDialogVisible = false">
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                </h2>
                
                <!-- Content -->
                <div class="p-6 overflow-y-auto max-h-[70vh]">
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Tên công ty (Name)</label>
                            <el-input v-model="companyForm.name" placeholder="VD: Công ty TNHH MTV ABC"></el-input>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">Giá trị (Value)</label>
                            <el-input-number v-model="companyForm.value" :min="0" :max="99" class="w-full" controls-position="right"></el-input-number>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="sticky bottom-0 w-full flex items-center justify-center gap-3 p-4 border-t border-gray-100 bg-white/95 backdrop-blur-md md:rounded-b-2xl shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <button @click="saveCompany()" :disabled="isSavingCompany" class="min-w-[140px] py-2.5 px-6 bg-brand text-white font-bold rounded-xl shadow-[0_4px_10px_rgba(2,75,179,0.3)] hover:-translate-y-[1px] hover:bg-brand-light hover:shadow-[0_6px_14px_rgba(2,75,179,0.4)] transition-all flex justify-center items-center">
                        <span v-if="!isSavingCompany">{{ editingCompany ? 'Cập nhật' : 'Tạo mới' }}</span>
                        <div v-else class="flex text-sm items-center gap-2">
                            <i class="el-icon-loading"></i> Đang tải
                        </div>
                    </button>
                    <button @click="companyDialogVisible = false" class="min-w-[100px] py-2.5 px-6 font-bold rounded-xl border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex justify-center items-center">
                        Đóng
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const API_URL = process.env.VUE_APP_API_URL;

export default {
    props: {
        isShow: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isVisible: true,
            activeTab: 'users',
            users: [],
            roles: [],
            departments: [],
            companies: [],
            isLoading: false,
            
            editDialogVisible: false,
            editingUser: null,
            editForm: {
                user_role_id: null,
                department_ids: [],
                store_ids: [],
                is_active: false,
                is_active_2fa: false,
                MAX_SIZE_FILE: 5
            },
            isSaving: false,
            storeOptions: [],
            isLoadingStoreOptions: false,

            createUserDialogVisible: false,
            showCreatePassword: false,
            createUserForm: {
                name: '',
                email: '',
                password: '',
                user_role_id: null,
                department_ids: [],
                is_active: true,
                MAX_SIZE_FILE: 5
            },
            isCreatingUser: false,

            // Store management
            stores: [],
            isLoadingStores: false,
            storeSearch: '',
            storePagination: { total: 0, page: 1, pageSize: 20 },
            storeDialogVisible: false,
            editingStore: null,
            storeForm: {
                code: '',
                shortaddress: '',
                fulladdress: '',
                storeid: null,
                brandName: '',
                brandCode: ''
            },
            isSavingStore: false,
            
            roleDialogVisible: false,
            editingRole: null,
            roleForm: {
                name: '',
                value: 0
            },
            isSavingRole: false,
            
            departmentDialogVisible: false,
            editingDepartment: null,
            departmentForm: {
                name: '',
                value: 0
            },
            isSavingDepartment: false,
            
            companyDialogVisible: false,
            editingCompany: null,
            companyForm: {
                name: '',
                value: 0
            },
            isSavingCompany: false,
            
            themeForm: {
                brandColor: 'var(--color-brand)',
                logoUrl: '',
                logoId: null,
                loginLogoUrl: '',
                loginLogoId: null,
                appName: 'HIGHLAND AGRI',
                headerBillUrl: '',
                headerBillId: null,
                loginPortalLabel: 'PORTAL MANAGEMENT',
                loginTitle: 'Hệ Thống Phiếu Chi Điện Tử',
                loginSubtitle: 'Giải pháp quản lý tài chính toàn diện. Tối ưu hóa quy trình, kiểm soát chi phí chặt chẽ và an toàn thông tin tuyệt đối.',
                loginCopyright: '© 2024 HIGHLAND AGRI. All rights reserved.'
            },
            isSavingTheme: false,
            isUploadingLogo: false,
            isUploadingLoginLogo: false,
            isUploadingHeaderBill: false
        };
    },
    async mounted() {
        if (this.isShow) {
            await this.fetchData();
        }
    },
    watch: {
        activeTab(val) {
            if (val === 'stores') {
                this.fetchStores();
            }
        }
    },
    methods: {
        async fetchStoreOptions() {
            this.isLoadingStoreOptions = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/\"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };
                const res = await this.$axios.get(`${API_URL}/stores/admin`, {
                    headers,
                    params: { page: 1, pageSize: 500 }
                });
                if (res.data && res.data.success) {
                    this.storeOptions = res.data.data;
                }
            } catch (err) {
                console.error('Lỗi tải store options:', err);
            } finally {
                this.isLoadingStoreOptions = false;
            }
        },
        async fetchStores() {
            this.isLoadingStores = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };
                const res = await this.$axios.get(`${API_URL}/stores/admin`, {
                    headers,
                    params: { page: this.storePagination.page, pageSize: this.storePagination.pageSize, search: this.storeSearch }
                });
                if (res.data && res.data.success) {
                    this.stores = res.data.data;
                    this.storePagination = { ...this.storePagination, ...res.data.pagination };
                }
            } catch (err) {
                console.error('Lỗi tải cửa hàng:', err);
                this.$notify.error({ title: 'Lỗi', message: 'Không thể tải danh sách cửa hàng' });
            } finally {
                this.isLoadingStores = false;
            }
        },
        onStorePageChange(page) {
            this.storePagination.page = page;
            this.fetchStores();
        },
        openStoreDialog(store = null) {
            this.editingStore = store;
            if (store) {
                this.storeForm = {
                    code: store.code || '',
                    shortaddress: store.shortaddress || '',
                    fulladdress: store.fulladdress || '',
                    storeid: store.storeid || null,
                    brandName: (store.brand && store.brand.name) ? store.brand.name : '',
                    brandCode: (store.brand && store.brand.code) ? store.brand.code : ''
                };
            } else {
                this.storeForm = { code: '', shortaddress: '', fulladdress: '', storeid: null, brandName: '', brandCode: '' };
            }
            this.storeDialogVisible = true;
        },
        async saveStore() {
            if (!this.storeForm.code || !this.storeForm.shortaddress || !this.storeForm.fulladdress) {
                this.$notify.warning({ title: 'Chú ý', message: 'Vui lòng nhập đủ Mã, Tên và Địa chỉ' });
                return;
            }
            this.isSavingStore = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };
                const brand = (this.storeForm.brandName || this.storeForm.brandCode)
                    ? { name: this.storeForm.brandName, code: this.storeForm.brandCode }
                    : null;
                const payload = {
                    code: this.storeForm.code.trim(),
                    shortaddress: this.storeForm.shortaddress.trim(),
                    fulladdress: this.storeForm.fulladdress.trim(),
                    storeid: this.storeForm.storeid || null,
                    brand
                };
                let res;
                if (this.editingStore) {
                    res = await this.$axios.put(`${API_URL}/stores/admin/${this.editingStore.id}`, payload, { headers });
                } else {
                    res = await this.$axios.post(`${API_URL}/stores/admin`, payload, { headers });
                }
                if (res.data && res.data.success) {
                    this.$notify.success({ title: 'Thành công', message: res.data.message });
                    this.storeDialogVisible = false;
                    await this.fetchStores();
                } else {
                    this.$notify.error({ title: 'Thất bại', message: res.data.message || 'Không thể lưu cửa hàng' });
                }
            } catch (err) {
                console.error('Lỗi lưu cửa hàng:', err);
                this.$notify.error({ title: 'Thất bại', message: err.response?.data?.message || 'Đã có lỗi xảy ra' });
            } finally {
                this.isSavingStore = false;
            }
        },
        async deleteStore(store) {
            this.$confirm(`Xóa cửa hàng "${store.shortaddress}" (${store.code})?`, 'Xác nhận xóa', {
                confirmButtonText: 'Đồng ý xóa', cancelButtonText: 'Hủy', type: 'warning'
            }).then(async () => {
                try {
                    const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                    const headers = { 'X-Authorization': `Bearer ${token}` };
                    const res = await this.$axios.delete(`${API_URL}/stores/admin/${store.id}`, { headers });
                    if (res.data && res.data.success) {
                        this.$notify.success({ title: 'Thành công', message: 'Đã xóa cửa hàng' });
                        await this.fetchStores();
                    }
                } catch (err) {
                    this.$notify.error({ title: 'Thất bại', message: 'Không thể xóa cửa hàng này' });
                }
            }).catch(() => {});
        },
        async handleLogoUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            
            this.isUploadingLogo = true;
            try {
                const formData = new FormData();
                formData.append('files', file);
                
                const headers = { 
                    'Content-Type': 'multipart/form-data'
                };
                
                const res = await this.$axios.post(`${API_URL}/upload`, formData, { headers });
                if (res.data && res.data.length > 0) {
                    const fileData = res.data[0];
                    const fileName = (fileData.hash && fileData.ext)
                        ? `${fileData.hash}${fileData.ext}`
                        : (fileData.url ? fileData.url.split('/').pop() : fileData.name);
                    const uploadedUrl = `https://s3.ap-southeast-1.amazonaws.com/guta.content/${fileName}`;
                    this.themeForm.logoUrl = uploadedUrl;
                    this.themeForm.logoId = fileData.id;
                    this.$notify.success({ title: 'Thành công', message: 'Đã tải ảnh lên. Hãy nhấn Lưu cài đặt.' });
                }
            } catch (error) {
                console.error("Lỗi khi tải ảnh:", error);
                this.$notify.error({ title: 'Thất bại', message: 'Lỗi tải ảnh lên.' });
            } finally {
                this.isUploadingLogo = false;
                event.target.value = ''; // Reset input
            }
        },
        async removeLogo() {
            if (this.themeForm.logoId) {
                try {
                    await this.$axios.delete(`${API_URL}/upload/files/${this.themeForm.logoId}`);
                } catch (error) {
                    console.error("Lỗi xoá file S3:", error);
                }
            }
            this.themeForm.logoUrl = '';
            this.themeForm.logoId = null;
            await this.saveTheme();
        },
        async handleLoginLogoUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            
            this.isUploadingLoginLogo = true;
            try {
                const formData = new FormData();
                formData.append('files', file);
                
                const headers = { 
                    'Content-Type': 'multipart/form-data'
                };
                
                const res = await this.$axios.post(`${API_URL}/upload`, formData, { headers });
                if (res.data && res.data.length > 0) {
                    const fileData = res.data[0];
                    const fileName = (fileData.hash && fileData.ext)
                        ? `${fileData.hash}${fileData.ext}`
                        : (fileData.url ? fileData.url.split('/').pop() : fileData.name);
                    const uploadedUrl = `https://s3.ap-southeast-1.amazonaws.com/guta.content/${fileName}`;
                    this.themeForm.loginLogoUrl = uploadedUrl;
                    this.themeForm.loginLogoId = fileData.id;
                    this.$notify.success({ title: 'Thành công', message: 'Đã tải logo trang đăng nhập lên. Hãy nhấn Lưu cài đặt.' });
                }
            } catch (error) {
                console.error("Lỗi khi tải ảnh:", error);
                this.$notify.error({ title: 'Thất bại', message: 'Lỗi tải ảnh lên.' });
            } finally {
                this.isUploadingLoginLogo = false;
                event.target.value = ''; // Reset input
            }
        },
        async removeLoginLogo() {
            if (this.themeForm.loginLogoId) {
                try {
                    await this.$axios.delete(`${API_URL}/upload/files/${this.themeForm.loginLogoId}`);
                } catch (error) {
                    console.error("Lỗi xoá file S3:", error);
                }
            }
            this.themeForm.loginLogoUrl = '';
            this.themeForm.loginLogoId = null;
            await this.saveTheme();
        },
        async handleHeaderBillUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            
            this.isUploadingHeaderBill = true;
            try {
                const formData = new FormData();
                formData.append('files', file);
                
                const headers = { 
                    'Content-Type': 'multipart/form-data'
                };
                
                const res = await this.$axios.post(`${API_URL}/upload`, formData, { headers });
                if (res.data && res.data.length > 0) {
                    const fileData = res.data[0];
                    const fileName = (fileData.hash && fileData.ext)
                        ? `${fileData.hash}${fileData.ext}`
                        : (fileData.url ? fileData.url.split('/').pop() : fileData.name);
                    const uploadedUrl = `https://s3.ap-southeast-1.amazonaws.com/guta.content/${fileName}`;
                    this.themeForm.headerBillUrl = uploadedUrl;
                    this.themeForm.headerBillId = fileData.id;
                    this.$notify.success({ title: 'Thành công', message: 'Đã tải ảnh lên. Hãy nhấn Lưu cài đặt.' });
                }
            } catch (error) {
                console.error("Lỗi khi tải ảnh:", error);
                this.$notify.error({ title: 'Thất bại', message: 'Lỗi tải ảnh lên.' });
            } finally {
                this.isUploadingHeaderBill = false;
                event.target.value = ''; // Reset input
            }
        },
        async removeHeaderBill() {
            if (this.themeForm.headerBillId) {
                try {
                    await this.$axios.delete(`${API_URL}/upload/files/${this.themeForm.headerBillId}`);
                } catch (error) {
                    console.error("Lỗi xoá file S3:", error);
                }
            }
            this.themeForm.headerBillUrl = '';
            this.themeForm.headerBillId = null;
            await this.saveTheme();
        },
        async saveTheme() {
            this.isSavingTheme = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };
                
                const res = await this.$axios.put(`${API_URL}/theme`, {
                    brandColor: this.themeForm.brandColor,
                    logoUrl: this.themeForm.logoUrl,
                    logoId: this.themeForm.logoId,
                    loginLogoUrl: this.themeForm.loginLogoUrl,
                    loginLogoId: this.themeForm.loginLogoId,
                    appName: this.themeForm.appName,
                    headerBillUrl: this.themeForm.headerBillUrl,
                    headerBillId: this.themeForm.headerBillId,
                    loginPortalLabel: this.themeForm.loginPortalLabel,
                    loginTitle: this.themeForm.loginTitle,
                    loginSubtitle: this.themeForm.loginSubtitle,
                    loginCopyright: this.themeForm.loginCopyright
                }, { headers });
                
                if (res.data && res.data.success) {
                    this.$notify.success({ title: 'Thành công', message: 'Đã lưu cài đặt giao diện' });

                    // Chuẩn hóa và lưu vào $app.theme
                    const newTheme = this.$app._normalizeTheme(this.themeForm);
                    this.$app.theme = newTheme;

                    // Apply CSS variables ngay lập tức
                    this.$app._applyThemeCss(newTheme);

                    // Cập nhật localStorage để lần load sau không bị flash màu cũ
                    localStorage.setItem('app_theme', JSON.stringify(newTheme));
                }
            } catch (error) {
                console.error("Lỗi khi lưu theme:", error.response?.data || error);
                const errMsg = error.response?.data?.error?.message || error.response?.data?.message || 'Không thể lưu cài đặt giao diện';
                this.$notify.error({ title: 'Thất bại', message: errMsg });
            } finally {
                this.isSavingTheme = false;
            }
        },

        closePopup() {
            this.isVisible = false;
            setTimeout(() => {
                this.$emit('closed');
            }, 300);
        },
        async fetchData() {
            this.isLoading = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = {
                    'X-Authorization': `Bearer ${token}`
                };

                // Lấy config theme
                const themeRes = await this.$axios.get(`${API_URL}/theme`, { headers });
                if (themeRes.data) {
                    this.themeForm.brandColor = themeRes.data.brandColor || 'var(--color-brand)';
                    this.themeForm.logoUrl = themeRes.data.logoUrl || '';
                    this.themeForm.logoId = themeRes.data.logoId || null;
                    this.themeForm.loginLogoUrl = themeRes.data.loginLogoUrl || '';
                    this.themeForm.loginLogoId = themeRes.data.loginLogoId || null;
                    this.themeForm.appName = themeRes.data.appName || 'HIGHLAND AGRI';
                    this.themeForm.headerBillUrl = themeRes.data.headerBillUrl || '';
                    this.themeForm.headerBillId = themeRes.data.headerBillId || null;
                    this.themeForm.loginPortalLabel = themeRes.data.loginPortalLabel || 'PORTAL MANAGEMENT';
                    this.themeForm.loginTitle = themeRes.data.loginTitle || 'Hệ Thống Phiếu Chi Điện Tử';
                    this.themeForm.loginSubtitle = themeRes.data.loginSubtitle || 'Giải pháp quản lý tài chính toàn diện. Tối ưu hóa quy trình, kiểm soát chi phí chặt chẽ và an toàn thông tin tuyệt đối.';
                    this.themeForm.loginCopyright = themeRes.data.loginCopyright || '© 2024 HIGHLAND AGRI. All rights reserved.';
                }

                // Lấy danh sách Role
                const rolesRes = await this.$axios.get(`${API_URL}/user-roles/admin`, { headers });
                if (rolesRes.data && rolesRes.data.data) {
                    this.roles = rolesRes.data.data.map(r => ({ id: r.id, ...r.attributes }));
                }

                // Lấy danh sách Department
                const depsRes = await this.$axios.get(`${API_URL}/authuserslog/departments`, { headers });
                if (depsRes.data && depsRes.data.data) {
                    this.departments = depsRes.data.data.map(d => ({ id: d.id, ...d.attributes }));
                }

                // Lấy danh sách Company
                const companiesRes = await this.$axios.get(`${API_URL}/companies-lists/admin`, { headers });
                if (companiesRes.data && companiesRes.data.data) {
                    this.companies = companiesRes.data.data.map(c => ({ id: c.id, ...c.attributes }));
                }

                // Lấy danh sách User
                const usersRes = await this.$axios.get(`${API_URL}/authuserslog/all-users`, { headers });
                
                if (usersRes.data && usersRes.data.data) {
                    this.users = usersRes.data.data.map(u => {
                        // Backend dùng entityService trả về trực tiếp relation object/arrays
                        return {
                            id: u.id,
                            ...u.attributes,
                            user_role: u.attributes.user_role || null,
                            departments: u.attributes.departments || []
                        }
                    });
                } else if (usersRes.data && usersRes.data.length >= 0) {
                    // Trong trường hợp API trả về mảng trực tiếp (nếu không theo chuẩn Strapi v4)
                    this.users = usersRes.data;
                }
            } catch (error) {
                console.error("Lỗi khi tải dữ liệu admin:", error);
                this.$notify.error({
                    title: 'Lỗi',
                    message: 'Không thể tải dữ liệu người dùng. Vui lòng kiểm tra quyền Admin.'
                });
            } finally {
                this.isLoading = false;
            }
        },
        openEditDialog(user) {
            this.editingUser = user;
            this.editForm = {
                email: user.email || '',
                name: user.name || '',
                password: '',
                user_role_id: user.user_role ? user.user_role.id : (user.user_role || null),
                department_ids: user.departments ? user.departments.map(d => d.id) : [],
                store_ids: user.stores ? user.stores.map(s => s.id) : [],
                is_active: !!user.is_active,
                is_active_2fa: !!user.is_active_2fa,
                MAX_SIZE_FILE: user.MAX_SIZE_FILE || 5
            };
            // Load store options nếu chưa có
            if (this.storeOptions.length === 0) {
                this.fetchStoreOptions();
            }
            this.editDialogVisible = true;
        },
        async saveUser() {
            if (!this.editForm.email || !this.editForm.email.trim()) {
                this.$notify.error({
                    title: 'Lỗi',
                    message: 'Email không được để trống'
                });
                return;
            }
            if (!this.editForm.name || !this.editForm.name.trim()) {
                this.$notify.error({
                    title: 'Lỗi',
                    message: 'Tên người dùng không được để trống'
                });
                return;
            }

            this.isSaving = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = {
                    'X-Authorization': `Bearer ${token}`
                };

                // Strapi v4 update payload format { data: { ... } }
                const payload = {
                    data: {
                        email: this.editForm.email.trim(),
                        name: this.editForm.name.trim(),
                        ...(this.editForm.password && this.editForm.password.trim() ? { password: this.editForm.password.trim() } : {}),
                        user_role: this.editForm.user_role_id,
                        departments: this.editForm.department_ids,
                        stores: this.editForm.store_ids,
                        is_active: this.editForm.is_active,
                        is_active_2fa: this.editForm.is_active_2fa,
                        MAX_SIZE_FILE: this.editForm.MAX_SIZE_FILE
                    }
                };

                const res = await this.$axios.put(`${API_URL}/authuserslog/update-user/${this.editingUser.id}`, payload, { headers });
                
                if (res.status === 200 || res.data) {
                    this.$notify.success({
                        title: 'Thành công',
                        message: 'Đã cập nhật thông tin người dùng'
                    });
                    this.editDialogVisible = false;
                    await this.fetchData(); // Tải lại danh sách sau khi cập nhật
                }
            } catch (error) {
                console.error("Lỗi khi cập nhật user:", error);
                this.$notify.error({
                    title: 'Cập nhật thất bại',
                    message: error.response?.data?.error?.message || 'Đã có lỗi xảy ra. Hãy chắc chắn bạn có quyền thực hiện.'
                });
            } finally {
                this.isSaving = false;
            }
        },
        openCreateUserDialog() {
            this.createUserForm = {
                name: '',
                email: '',
                password: '',
                user_role_id: null,
                department_ids: [],
                is_active: true,
                MAX_SIZE_FILE: 5
            };
            this.showCreatePassword = false;
            this.createUserDialogVisible = true;
        },
        async saveNewUser() {
            if (!this.createUserForm.name || !this.createUserForm.email || !this.createUserForm.password) {
                this.$notify.warning({ title: 'Chú ý', message: 'Vui lòng nhập đủ Họ tên, Email và Mật khẩu' });
                return;
            }
            this.isCreatingUser = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };
                const res = await this.$axios.post(`${API_URL}/authuserslog/create-user`, {
                    name: this.createUserForm.name,
                    email: this.createUserForm.email,
                    password: this.createUserForm.password,
                    user_role_id: this.createUserForm.user_role_id,
                    department_ids: this.createUserForm.department_ids,
                    is_active: this.createUserForm.is_active,
                    MAX_SIZE_FILE: this.createUserForm.MAX_SIZE_FILE
                }, { headers });
                if (res.data && res.data.success) {
                    this.$notify.success({ title: 'Thành công', message: `Đã tạo tài khoản cho ${res.data.user.name}` });
                    this.createUserDialogVisible = false;
                    await this.fetchData();
                } else {
                    this.$notify.error({ title: 'Thất bại', message: res.data.message || 'Không thể tạo tài khoản' });
                }
            } catch (error) {
                console.error('Lỗi tạo user:', error);
                this.$notify.error({ title: 'Thất bại', message: error.response?.data?.message || 'Đã có lỗi xảy ra' });
            } finally {
                this.isCreatingUser = false;
            }
        },
        openRoleDialog(role) {
            this.editingRole = role;
            if (role) {
                this.roleForm = {
                    name: role.name,
                    value: role.value
                };
            } else {
                this.roleForm = {
                    name: '',
                    value: 0
                };
            }
            this.roleDialogVisible = true;
        },
        async saveRole() {
            this.isSavingRole = true;
            try {
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };
                
                const payload = {
                    data: {
                        name: this.roleForm.name,
                        value: this.roleForm.value
                    }
                };

                let res;
                if (this.editingRole) {
                    res = await this.$axios.put(`${API_URL}/user-roles/admin/${this.editingRole.id}`, payload, { headers });
                } else {
                    res = await this.$axios.post(`${API_URL}/user-roles/admin`, payload, { headers });
                }

                if (res.status === 200 || res.data) {
                    this.$notify.success({
                        title: 'Thành công',
                        message: this.editingRole ? 'Đã cập nhật chức vụ' : 'Đã tạo chức vụ mới'
                    });
                    this.roleDialogVisible = false;
                    await this.fetchData(); // Tải lại danh sách sau khi cập nhật
                }
            } catch (error) {
                console.error("Lỗi khi lưu chức vụ:", error);
                this.$notify.error({
                    title: 'Thất bại',
                    message: error.response?.data?.error?.message || 'Đã có lỗi xảy ra.'
                });
            } finally {
                this.isSavingRole = false;
            }
        },
        async deleteRole(role) {
            this.$confirm(`Bạn có chắc chắn muốn xoá chức vụ "${role.name}" không? Thao tác này có thể ảnh hưởng đến phân quyền ứng dụng.`, 'Xác nhận xoá', {
                confirmButtonText: 'Đồng ý',
                cancelButtonText: 'Hủy',
                type: 'warning'
            }).then(async () => {
                try {
                    const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                    const headers = { 'X-Authorization': `Bearer ${token}` };
                    
                    await this.$axios.delete(`${API_URL}/user-roles/admin/${role.id}`, { headers });
                    
                    this.$notify.success({
                        title: 'Thành công',
                        message: 'Đã xoá chức vụ'
                    });
                    await this.fetchData();
                } catch (error) {
                    console.error("Lỗi khi xoá chức vụ:", error);
                    this.$notify.error({
                        title: 'Thất bại',
                        message: error.response?.data?.error?.message || 'Không thể xoá chức vụ này.'
                    });
                }
            }).catch(() => {
                // Hủy xoá
            });
        },
        openDepartmentDialog(department = null) {
            this.editingDepartment = department;
            if (department) {
                this.departmentForm = {
                    name: department.name,
                    value: department.value
                };
            } else {
                this.departmentForm = {
                    name: '',
                    value: 0
                };
            }
            this.departmentDialogVisible = true;
        },
        async saveDepartment() {
            if (!this.departmentForm.name || this.departmentForm.name.trim() === '') {
                this.$notify.warning({
                    title: 'Chú ý',
                    message: 'Vui lòng nhập tên phòng ban'
                });
                return;
            }

            try {
                this.isSavingDepartment = true;
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };

                const payload = {
                    data: {
                        name: this.departmentForm.name,
                        value: this.departmentForm.value
                    }
                };

                let res;
                if (this.editingDepartment) {
                    res = await this.$axios.put(`${API_URL}/departments/admin/${this.editingDepartment.id}`, payload, { headers });
                } else {
                    res = await this.$axios.post(`${API_URL}/departments/admin`, payload, { headers });
                }

                if (res.status === 200 || res.data) {
                    this.$notify.success({
                        title: 'Thành công',
                        message: this.editingDepartment ? 'Đã cập nhật phòng ban' : 'Đã tạo phòng ban mới'
                    });
                    this.departmentDialogVisible = false;
                    await this.fetchData();
                }
            } catch (error) {
                console.error("Lỗi khi lưu phòng ban:", error);
                this.$notify.error({
                    title: 'Thất bại',
                    message: error.response?.data?.error?.message || 'Đã có lỗi xảy ra.'
                });
            } finally {
                this.isSavingDepartment = false;
            }
        },
        async deleteDepartment(department) {
            this.$confirm(`Bạn có chắc chắn muốn xoá phòng ban "${department.name}" không?`, 'Xác nhận xoá', {
                confirmButtonText: 'Đồng ý',
                cancelButtonText: 'Hủy',
                type: 'warning'
            }).then(async () => {
                try {
                    const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                    const headers = { 'X-Authorization': `Bearer ${token}` };
                    
                    await this.$axios.delete(`${API_URL}/departments/admin/${department.id}`, { headers });
                    
                    this.$notify.success({
                        title: 'Thành công',
                        message: 'Đã xoá phòng ban'
                    });
                    await this.fetchData();
                } catch (error) {
                    console.error("Lỗi khi xoá phòng ban:", error);
                    this.$notify.error({
                        title: 'Thất bại',
                        message: error.response?.data?.error?.message || 'Không thể xoá phòng ban này.'
                    });
                }
            }).catch(() => {
                // Hủy xoá
            });
        },
        openCompanyDialog(company = null) {
            this.editingCompany = company;
            if (company) {
                this.companyForm = {
                    name: company.name,
                    value: company.value
                };
            } else {
                this.companyForm = {
                    name: '',
                    value: 0
                };
            }
            this.companyDialogVisible = true;
        },
        async saveCompany() {
            if (!this.companyForm.name || this.companyForm.name.trim() === '') {
                this.$notify.warning({
                    title: 'Chú ý',
                    message: 'Vui lòng nhập tên công ty'
                });
                return;
            }

            try {
                this.isSavingCompany = true;
                const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                const headers = { 'X-Authorization': `Bearer ${token}` };

                const payload = {
                    data: {
                        name: this.companyForm.name,
                        value: this.companyForm.value
                    }
                };

                let res;
                if (this.editingCompany) {
                    res = await this.$axios.put(`${API_URL}/companies-lists/admin/${this.editingCompany.id}`, payload, { headers });
                } else {
                    res = await this.$axios.post(`${API_URL}/companies-lists/admin`, payload, { headers });
                }

                if (res.status === 200 || res.data) {
                    this.$notify.success({
                        title: 'Thành công',
                        message: this.editingCompany ? 'Đã cập nhật công ty' : 'Đã tạo công ty mới'
                    });
                    this.companyDialogVisible = false;
                    await this.fetchData();
                }
            } catch (error) {
                console.error("Lỗi khi lưu công ty:", error);
                this.$notify.error({
                    title: 'Thất bại',
                    message: error.response?.data?.error?.message || 'Đã có lỗi xảy ra.'
                });
            } finally {
                this.isSavingCompany = false;
            }
        },
        async deleteCompany(company) {
            this.$confirm(`Bạn có chắc chắn muốn xoá công ty "${company.name}" không?`, 'Xác nhận xoá', {
                confirmButtonText: 'Đồng ý',
                cancelButtonText: 'Hủy',
                type: 'warning'
            }).then(async () => {
                try {
                    const token = this.$app.userInfo?.access_token || localStorage.getItem('uToken')?.replace(/"/g, '');
                    const headers = { 'X-Authorization': `Bearer ${token}` };
                    
                    await this.$axios.delete(`${API_URL}/companies-lists/admin/${company.id}`, { headers });
                    
                    this.$notify.success({
                        title: 'Thành công',
                        message: 'Đã xoá công ty'
                    });
                    await this.fetchData();
                } catch (error) {
                    console.error("Lỗi khi xoá công ty:", error);
                    this.$notify.error({
                        title: 'Thất bại',
                        message: error.response?.data?.error?.message || 'Không thể xoá công ty này.'
                    });
                }
            }).catch(() => {
                // Hủy xoá
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@keyframes popupFadeIn {
    0% {
        opacity: 0;
        transform: translateY(20px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}
@keyframes popupFadeOut {
    0% {
        opacity: 1;
        transform: translateY(0);
    }
    100% {
        opacity: 0;
        transform: translateY(20px);
    }
}
.animate-popupFadeIn {
    animation: popupFadeIn 0.3s forwards;
}
.animate-popupFadeOut {
    animation: popupFadeOut 0.3s forwards;
}

::v-deep .el-switch__core {
    border-radius: 12px;
}

::v-deep .el-table {
    .custom-row-head {
        th.custom-cell-head.el-table__cell {
            background-color: #f8fafc;
            border-right: 1px solid #f1f5f9;
            border-bottom: 1px solid #e2e8f0;
            color: #475569;
            font-weight: 600;
        }
    }

    &.el-table--border {
        border: none;
        border-bottom: 1px solid #f1f5f9;
    }
    
    td.el-table__cell {
        border-bottom: 1px solid #f1f5f9;
    }
}

::v-deep .custom-tabs {
    .el-tabs__nav-wrap::after {
        height: 1px;
        background-color: #f1f5f9;
    }
    .el-tabs__item {
        font-weight: 600;
        color: #64748b;
        font-size: 15px;
        padding: 0 20px;
        &.is-active {
            color: var(--color-brand);
        }
    }
    .el-tabs__active-bar {
        background-color: var(--color-brand);
        height: 3px;
        border-radius: 3px 3px 0 0;
    }
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
}
.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
